import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

// ======================================================
// GENERAR VARIANTES AUTOMÁTICAS PARA ENTRENAMIENTO EN PAREJA
// ======================================================
function generarVariantesPareja(servicios, configPareja) {
  if (!configPareja) return [];

  const { descuento_global = 20, descuentos_por_nivel = {} } = configPareja;

  const servEntreno = servicios.find((s) => s.id === "entrenamiento");
  if (!servEntreno || !servEntreno.variantes) return [];

  const variantesPareja = [];

  Object.entries(servEntreno.variantes).forEach(([nivel, lista]) => {
    const desc = descuentos_por_nivel[nivel] ?? descuento_global;

    lista.forEach((v) => {
      const precioBase = Number(v.precio || 0);
      const precioDescontado = Math.round(precioBase * (1 - desc / 100));

      variantesPareja.push({
        id: `pareja-${v.id}`,
        nombre: `${v.nombre} (Pareja)`,
        precio: precioDescontado * 2,
        precio_original_individual: precioBase,
        descuento_aplicado: desc,
        nivel,
        modalidadesPermitidas:{
          privada:true,
          grupal:false,
          },  // ← hereda de la variante original
      });
    });
  });

  return variantesPareja;
}

export default function useServicios() {
  const [servicios, setServicios] = useState([]);
  const [serviciosFlat, setServiciosFlat] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "editar_servicios"), orderBy("orden"));

    const unsub = onSnapshot(q, async (snap) => {
    const data = snap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      .filter((s) => s.eliminado !== true && s.activo !== false);

      let configPareja = null;

      try {
        const cfgSnap = await getDoc(doc(db, "config", "pareja_precios"));
        if (cfgSnap.exists()) configPareja = cfgSnap.data();
      } catch (err) {
        console.error("Error cargando config pareja:", err);
      }

      const serviciosProcesados = data.map((s) => {
        if (s.id === "pareja") {
          return {
            ...s,
            variantes: generarVariantesPareja(data, configPareja),
          };
        }
        return s;
      });

      setServicios(serviciosProcesados);

      const flat = serviciosProcesados.flatMap((serv) => {
        const servicioDocId = serv.id;
        const categoria = serv.title?.trim() || "Servicio";

        // Caso 1 — variantes simple array
        if (Array.isArray(serv.variantes)) {
          return serv.variantes.map((v, i) => ({
            id: `${servicioDocId}::${v.id || `${servicioDocId}-${i}`}`,
            servicioDocId,
            varianteId: v.id || "",
            categoria,
            variante: v.nombre || "",
            precio: Number(v.precio || 0),
            precio_grupo: v.precio_grupo == null ? null : Number(v.precio_grupo),
            precio_privada: v.precio_privada == null ? null : Number(v.precio_privada),
            modalidadesPermitidas: v.modalidadesPermitidas || null,  // ← nuevo
            display: `${categoria} — ${v.nombre}: $${Number(v.precio || 0).toLocaleString("es-CL")}`,
            activo: serv.activo !== false,
            orden: serv.orden ?? 999,
            image: serv.image || "",
            desc: serv.desc || "",
            extra: serv.extra || "",
          }));
        }

        // Caso 2 — variantes agrupadas por nivel
        if (serv.variantes && typeof serv.variantes === "object") {
          return Object.entries(serv.variantes).flatMap(([grupo, arr]) =>
            (arr || []).map((p, j) => ({
              id: `${servicioDocId}::${p.id || `${servicioDocId}-${grupo}-${j}`}`,
              servicioDocId,
              varianteId: p.id || "",
              categoria: `${categoria} — ${grupo}`,
              variante: p.nombre || "",
              precio: Number(p.precio || 0),
              precio_grupo: p.precio_grupo == null ? null : Number(p.precio_grupo),
              precio_privada: p.precio_privada == null ? null : Number(p.precio_privada),
              modalidadesPermitidas: p.modalidadesPermitidas || null,  // ← nuevo
              display: `${categoria} — ${grupo} — ${p.nombre}: $${Number(p.precio || 0).toLocaleString("es-CL")}`,
              grupo,
              activo: serv.activo !== false,
              orden: serv.orden ?? 999,
              image: serv.image || "",
              desc: serv.desc || "",
              extra: serv.extra || "",
            }))
          );
        }

        return [];
      });

      setServiciosFlat(flat);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  return { servicios, serviciosFlat, loading };
}